// src/components/AdditionalServices.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calculator, 
  BookOpen, 
  FileText, 
  TrendingUp,
  Users,
  Shield,
  Zap,
  ArrowRight,
  Lock
} from 'lucide-react';
import { signInWithPopup } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase';
import DropshippingBookkeeping from './DropshippingBookkeeping';
import './AdditionalServices.css';

const AdditionalServices = ({ user, userProfile, setIsNicknameSetupOpen }) => {
  const [activeService, setActiveService] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const navigate = useNavigate();

  // 구글 로그인
  const handleGoogleLogin = async () => {
    setAuthLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // 기존 사용자 프로필 확인
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // 새 사용자인 경우 닉네임 설정 팝업 열기
        setIsNicknameSetupOpen(true);
      }
    } catch (error) {
      console.error('로그인 오류:', error);
      alert('로그인 중 오류가 발생했습니다.');
    } finally {
      setAuthLoading(false);
    }
  };

  const services = [
    {
      id: 'bookkeeping',
      title: '드롭시핑 간편장부',
      description: '개인사업자를 위한 간편한 매출 및 지출 관리 시스템',
      icon: Calculator,
      features: [
        '수입/지출 기록 관리',
        'Excel/PDF 출력',
        '월별 손익 요약',
        '계정과목 자동 분류'
      ],
      requiresLogin: true,
      comingSoon: false
    },
    {
      id: 'inventory',
      title: '재고 관리',
      description: '상품 재고 현황 및 발주 관리',
      icon: BookOpen,
      features: [
        '상품별 재고 현황',
        '입출고 내역 관리',
        '자동 발주 알림',
        '재고 분석 리포트'
      ],
      requiresLogin: true,
      comingSoon: true
    },
    {
      id: 'analytics',
      title: '매출 분석',
      description: '매출 데이터 분석 및 트렌드 분석',
      icon: TrendingUp,
      features: [
        '매출 트렌드 분석',
        '상품별 성과 분석',
        '고객 분석',
        '수익성 분석'
      ],
      requiresLogin: true,
      comingSoon: true
    },
    {
      id: 'crm',
      title: '고객 관리',
      description: '고객 정보 및 주문 이력 관리',
      icon: Users,
      features: [
        '고객 정보 관리',
        '주문 이력 추적',
        '고객 세분화',
        '마케팅 자동화'
      ],
      requiresLogin: true,
      comingSoon: true
    },
    {
      id: 'compliance',
      title: '세무/법무 가이드',
      description: '개인사업자 세무 및 법무 가이드',
      icon: Shield,
      features: [
        '세무 신고 가이드',
        '사업자 등록 안내',
        '법령 준수 체크리스트',
        '전문가 상담 연결'
      ],
      requiresLogin: false,
      comingSoon: true
    },
    {
      id: 'automation',
      title: '업무 자동화',
      description: '반복 업무 자동화 도구',
      icon: Zap,
      features: [
        '주문 처리 자동화',
        '재고 알림 자동화',
        '고객 응대 자동화',
        '리포트 자동 생성'
      ],
      requiresLogin: true,
      comingSoon: true
    }
  ];

  const handleServiceClick = (service) => {
    if (service.comingSoon) {
      alert('준비 중인 서비스입니다. 곧 출시 예정입니다!');
      return;
    }

    if (service.requiresLogin && !user) {
        if (window.confirm('로그인이 필요한 서비스입니다. 로그인하시겠습니까?')) {
            handleGoogleLogin();
        }
        return;
    }

    if (service.requiresLogin && !userProfile?.nickname) {
      setIsNicknameSetupOpen(true);
      return;
    }

    setActiveService(service.id);
  };

  // 간편장부 서비스가 활성화된 경우
  if (activeService === 'bookkeeping') {
    return (
      <div className="additional-services">
        <div className="service-header">
          <button 
            onClick={() => setActiveService(null)}
            className="back-button"
          >
            ← 부가서비스로 돌아가기
          </button>
          <h1>드롭시핑 간편장부</h1>
        </div>
        <DropshippingBookkeeping user={user} userProfile={userProfile} />
      </div>
    );
  }

  return (
    <div className="additional-services">
      <div className="services-header">
        <div className="header-content">
          <h1 className="services-title">부가서비스</h1>
          <p className="services-subtitle">
            WorldLand에서 제공하는 다양한 비즈니스 도구를 활용해보세요
          </p>
        </div>
      </div>

      <div className="services-grid">
        {services.map((service) => (
          <div 
            key={service.id} 
            className={`service-card ${service.comingSoon ? 'coming-soon' : ''}`}
            onClick={() => handleServiceClick(service)}
          >
            <div className="service-icon">
              <service.icon className="icon" />
              {service.requiresLogin && (
                <div className="login-badge">
                  <Lock size={12} />
                </div>
              )}
            </div>

            <div className="service-content">
              <h3 className="service-title">
                {service.title}
                {service.comingSoon && <span className="coming-soon-badge">준비중</span>}
              </h3>
              <p className="service-description">{service.description}</p>

              <ul className="service-features">
                {service.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>

              <div className="service-footer">
                {service.requiresLogin && !user ? (
                  <div className="login-required">
                    <Lock size={16} />
                    <span>로그인 필요</span>
                  </div>
                ) : (
                  <div className="service-action">
                    <span>{service.comingSoon ? '준비 중' : '시작하기'}</span>
                    <ArrowRight size={16} />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {!user && (
        <div className="login-promotion">
          <div className="promotion-content">
            <h3>더 많은 기능을 이용하려면 로그인하세요</h3>
            <p>로그인하시면 모든 부가서비스를 이용할 수 있습니다.</p>
            <button 
              onClick={handleGoogleLogin}
              disabled={authLoading}
              className="login-promotion-btn"
            >
              {authLoading ? '로그인 중...' : '구글 계정으로 로그인'}
            </button>
          </div>
        </div>
      )}

      <div className="services-info">
        <div className="info-section">
          <h3>부가서비스 안내</h3>
          <div className="info-grid">
            <div className="info-item">
              <Calculator className="info-icon" />
              <div>
                <h4>간편장부</h4>
                <p>개인사업자를 위한 매출/지출 관리 도구로 현재 이용 가능합니다.</p>
              </div>
            </div>
            <div className="info-item">
              <Zap className="info-icon" />
              <div>
                <h4>순차적 출시</h4>
                <p>다양한 비즈니스 도구들을 순차적으로 출시할 예정입니다.</p>
              </div>
            </div>
            <div className="info-item">
              <Shield className="info-icon" />
              <div>
                <h4>보안</h4>
                <p>모든 데이터는 안전하게 암호화되어 저장됩니다.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdditionalServices;